import React from "react";
import { Quote, Eye, Pencil, Trash2, Check, Ban } from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_PALETTE = [
  "#4361EE", "#EF4444", "#10B981", "#F97316",
  "#8B5CF6", "#F59E0B", "#14B8A6", "#3B82F6",
];

export const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

export const getAvatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
};

// ── Star Rating (display-only) ────────────────────────────────────────────────
export const StarRating = ({ rating = 5, max = 5, size = "md" }) => {
  const sz = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }, (_, i) => (
        <svg
          key={i}
          className={`${sz} ${i < rating ? "text-amber-400" : "text-gray-200"}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

// ── Testimonial Card ──────────────────────────────────────────────────────────
const TestimonialCard = ({ testimonial, onView, onEdit, onDelete, onApprove, onReject }) => {
  const { full_name, job_title, company, rating = 5, content, created_at, createdAt, status, is_featured } = testimonial;
  const initials = getInitials(full_name);
  const bgColor  = getAvatarColor(full_name);

  const statusBadge = status === "approved"
    ? "bg-emerald-50 text-emerald-600"
    : status === "rejected"
    ? "bg-red-50 text-red-500"
    : "bg-amber-50 text-amber-600";

  return (
    <div className="bg-white border border-surface-border rounded-2xl p-6 flex flex-col gap-3 hover:shadow-sm transition-shadow">
      {/* Header: avatar + name + badges */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
          style={{ backgroundColor: bgColor }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-brand-text">{full_name}</p>
            {is_featured && (
              <span className="text-[10px] font-semibold bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full">Featured</span>
            )}
            {status && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize ${statusBadge}`}>{status}</span>
            )}
          </div>
          <p className="text-xs text-brand-muted">
            {job_title} · {company}
          </p>
        </div>
      </div>

      {/* Stars */}
      <StarRating rating={rating} />

      {/* Quote + text */}
      <div className="flex flex-col gap-1.5">
        <Quote className="w-5 h-5 text-blue-200" />
        <p className="text-sm text-brand-text leading-relaxed line-clamp-4">{content}</p>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-auto pt-3 border-t border-surface-border">
        <span className="text-xs text-brand-light">
          Added {formatDate(createdAt || created_at)}
        </span>
        <div className="flex items-center gap-1.5">
          {status !== "approved" && (
            <button
              onClick={() => onApprove?.(testimonial)}
              className="p-1.5 rounded-lg text-brand-light hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
              title="Approve — publishes this on the public site"
            >
              <Check className="w-4 h-4" />
            </button>
          )}
          {status !== "rejected" && (
            <button
              onClick={() => onReject?.(testimonial)}
              className="p-1.5 rounded-lg text-brand-light hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Reject"
            >
              <Ban className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => onView?.(testimonial)}
            className="p-1.5 rounded-lg text-brand-light hover:text-primary hover:bg-blue-50 transition-colors"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => onEdit?.(testimonial)}
            className="p-1.5 rounded-lg text-brand-light hover:text-primary hover:bg-blue-50 transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete?.(testimonial)}
            className="p-1.5 rounded-lg text-brand-light hover:text-danger hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCard;
